import { Fragment } from 'react';
import type { AccessAction, AccessLogEntry } from '@shared/gmail';
import type { TeamMember } from '@shared/types';
import { dayLabel, formatNumber } from '@/lib/format';
import { Chip, type ChipTone } from '@/ui/bits';
import { firstName } from './queries';

// Rows of the inbox guard's access log, used in your account settings and in the admin console.

/** Names for the action filter. */
export const ACTION_LABELS: Record<AccessAction, string> = {
  open: 'Replies opened',
  check: 'Checked by thread ID',
  sync: 'Shared inbox and CRM reads',
  attachment: 'Attachments downloaded or copied',
  accept: 'Accepted by the CRM address',
  reject: 'Turned away by the CRM address',
  connect: 'Connected',
  disconnect: 'Disconnected',
};

export function actionChip(e: AccessLogEntry): { label: string; tone?: ChipTone } {
  const n = formatNumber(e.count);
  switch (e.action) {
    case 'open':
    case 'sync':
      return { label: `Opened ${n}`, tone: 'info' };
    case 'check':
      return { label: `Checked ${n}` };
    case 'attachment':
      return { label: e.count > 1 ? `Attachments ${n}` : 'Attachment', tone: 'info' };
    case 'accept':
      return { label: e.count > 1 ? `Accepted ${n}` : 'Accepted', tone: 'success' };
    case 'reject':
      return { label: e.count > 1 ? `Turned away ${n}` : 'Turned away', tone: 'warning' };
    case 'connect':
      return { label: 'Connected', tone: 'success' };
    case 'disconnect':
      return { label: 'Disconnected' };
  }
}

const clockFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

/** "09:04:12", the way the design shows log times. */
export function logClock(at: string): string {
  return clockFmt.format(new Date(at));
}

/** "Maya's inbox", "hello@ shared" or the CRM address. */
export function inboxLabel(e: Pick<AccessLogEntry, 'mailboxKind' | 'mailboxAddress' | 'mailboxUserId'>, byId: Map<string, TeamMember>): string {
  if (e.mailboxKind === 'personal') {
    const owner = e.mailboxUserId ? byId.get(e.mailboxUserId) : undefined;
    return owner ? `${firstName(owner.name)}'s inbox` : e.mailboxAddress;
  }
  if (e.mailboxKind === 'shared') return `${e.mailboxAddress.split('@')[0]}@ shared`;
  return e.mailboxAddress;
}

/**
 * Log rows grouped by day. `own` leaves out the inbox column (everything in your own log is your inbox).
 */
export function LogRows({ entries, byId, own }: { entries: AccessLogEntry[]; byId: Map<string, TeamMember>; own?: boolean }) {
  let lastDay = '';
  return (
    <>
      {entries.map((e) => {
        const day = dayLabel(e.at);
        const head = day !== lastDay;
        lastDay = day;
        const chip = actionChip(e);
        return (
          <Fragment key={e.id}>
            {head ? <div className="gm-day">{day}</div> : null}
            <div className={own ? 'log-row gm-own' : 'log-row'}>
              <time dateTime={e.at}>{logClock(e.at)}</time>
              {own ? null : (
                <span className="lw" title={e.mailboxAddress}>
                  {inboxLabel(e, byId)}
                </span>
              )}
              <span className="lx">
                <Chip tone={chip.tone}>{chip.label}</Chip>
                {e.detail ?? ACTION_LABELS[e.action]}
              </span>
            </div>
          </Fragment>
        );
      })}
    </>
  );
}
